import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { Section, Field, NativeSelect, StatusBadge } from "@/components/common";
import { FormDialog, AccountSelect } from "./common";

const TYPES = ["asset", "liability", "equity", "income", "expense"].map((t) => ({ value: t, label: t }));
const SUBTYPES = [{ value: "", label: "—" }, ...["cash", "bank", "clearing", "receivable", "payable", "tax"].map((t) => ({ value: t, label: t }))];

function PaymentAccounts({ accounts }) {
  const [s, reload] = useLoad("/acct/settings");
  const [m, setM] = useState(null);
  const { canWrite } = useAccess();
  useEffect(() => { if (s) setM(s.payment_accounts); }, [s]);
  if (!s || !m) return null;
  const save = async () => { if (await act(() => api.put("/acct/settings/payment-accounts", { payment_accounts: m }), "Payment accounts saved")) reload(); };
  return (
    <Section title="Payment method accounts" testId="payment-accounts">
      <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-5">
        {Object.entries(s.payment_methods).map(([k, l]) => <Field key={k} label={l}><AccountSelect testId={`pm-account-${k}`} accounts={accounts} types={["asset"]} value={m[k]} onChange={(v) => setM({ ...m, [k]: v })} /></Field>)}
      </div>
      {canWrite(["tenant_admin"]) && <div className="border-t border-slate-200 px-5 py-3"><Button data-testid="pm-accounts-save" onClick={save} className="bg-[#13294B] hover:bg-[#1c3a66]">Save mapping</Button></div>}
    </Section>
  );
}

export default function Coa({ lk, reloadLk }) {
  const [rows, reload] = useLoad("/acct/accounts");
  const [f, setF] = useState(null);
  const { canWrite, currency } = useAccess();
  const can = canWrite(["tenant_admin", "accountant"]);
  const save = async () => {
    const r = await act(() => (f.isNew ? api.post("/acct/accounts", f) : api.put(`/acct/accounts/${f.code}`, f)), "Account saved");
    if (r) { setF(null); reload(); reloadLk(); }
  };
  return (
    <>
      <Section title="Chart of accounts" testId="coa-section" action={can && <Button size="sm" data-testid="coa-add-button" onClick={() => setF({ isNew: true, code: "", name: "", type: "expense", subtype: "", active: true })}><Plus className="mr-1 h-4 w-4" />Add account</Button>}>
        <div className="overflow-x-auto"><table className="po-table" data-testid="coa-table">
          <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Subtype</th><th>Status</th><th className="po-num">Balance</th><th></th></tr></thead>
          <tbody>{rows?.map((a) => (
            <tr key={a.code} data-testid={`coa-row-${a.code}`}><td className="font-mono text-xs">{a.code}</td><td>{a.name}{a.system && <span className="ml-2 text-[10px] uppercase text-slate-400">standard</span>}</td>
              <td className="capitalize">{a.type}</td><td className="capitalize text-slate-500">{a.subtype || "—"}</td><td><StatusBadge status={a.active ? "active" : "canceled"} label={a.active ? "active" : "inactive"} /></td>
              <td className="po-num">{fmt(a.balance, currency)}</td>
              <td>{can && <button data-testid={`coa-edit-${a.code}`} className="text-slate-400 hover:text-slate-700" onClick={() => setF({ ...a, subtype: a.subtype || "" })}><Pencil className="h-4 w-4" /></button>}</td></tr>))}</tbody>
        </table></div>
      </Section>
      <PaymentAccounts accounts={lk.accounts} />
      <FormDialog open={!!f} onClose={() => setF(null)} title={f?.isNew ? "New account" : `Edit ${f?.code}`} onSave={save} testId="coa-dialog" disabled={!f?.code || !f?.name}>
        {f && <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Code"><Input data-testid="coa-code-input" disabled={!f.isNew} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></Field>
          <Field label="Name"><Input data-testid="coa-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Type"><NativeSelect testId="coa-type-select" value={f.type} onChange={(v) => setF({ ...f, type: v })} options={TYPES} /></Field>
          <Field label="Subtype"><NativeSelect testId="coa-subtype-select" value={f.subtype} onChange={(v) => setF({ ...f, subtype: v })} options={SUBTYPES} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="coa-active-checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Active</label>
        </div>}
      </FormDialog>
    </>
  );
}
